import { usePasswordValidation } from '@lib/hooks/auth/password-criteria';
import { showToast } from '@lib/utils/toastmaster';
import { Divider } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { AuthErrorMessage } from '@pages/Auth/AuthErrorMessage';
import { AuthField } from '@pages/Auth/AuthField';
import { PasswordValidationComponent } from '@pages/Auth/PasswordValidation/PasswordValidationComponent';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import css from './ChangePassword.module.scss';

export const ChangePasswordForm = observer(function ChangePasswordForm({
  mobile,
}: {
  mobile?: boolean;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmedNewPassword, setConfirmedNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordValidation = usePasswordValidation(newPassword, confirmedNewPassword);
  // Magic-link users have no password yet — only bypass the current-password
  // requirement when the backend explicitly says so.
  const needsCurrentPassword = userStore.data?.hasPassword !== false;
  const allValid =
    (!needsCurrentPassword || currentPassword.length > 0) &&
    passwordValidation.every((condition) => condition.satisfied);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmedNewPassword('');
    setError(null);
  };

  const submit = async () => {
    if (needsCurrentPassword && currentPassword.length === 0) {
      setError('Current password is required.');
      return;
    }

    if (newPassword.length === 0) {
      setError('New password is required.');
      return;
    }

    if (newPassword !== confirmedNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordValidation.some((condition) => !condition.satisfied)) {
      setError('Please satisfy all the conditions for your account security.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await userStore.changePassword(needsCurrentPassword ? currentPassword : null, newPassword);
      showToast('password-change-success', { set: !needsCurrentPassword });
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred');
      showToast('password-change-failed', { set: !needsCurrentPassword, error: e });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classNames(css.changePasswordContainer, { [css.mobile]: mobile })}>
      <div className={css.changePasswordInnerContainer}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className={css.changePasswordForm}
        >
          {needsCurrentPassword && (
            <AuthField
              label="Current password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              autoFocus={!mobile}
            />
          )}
          <AuthField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoFocus={!mobile && !needsCurrentPassword}
          />
          <AuthField
            label="Confirm new password"
            type="password"
            value={confirmedNewPassword}
            onChange={(e) => setConfirmedNewPassword(e.target.value)}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
          />
          {error && <AuthErrorMessage message={error} />}

          <PasswordValidationComponent passwordValidation={passwordValidation} />

          {!mobile && <Divider sx={{ marginRight: '-24px' }} />}
          <div className={css.submitContainer}>
            <div className={css.innerSubmitContainer}>
              <ActionButton type="submit" disabled={loading || !allValid} fullWidth={mobile}>
                {loading ? 'Saving...' : 'Save changes'}
              </ActionButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});
